import {
  isSolarLightReachPricingRequiredError,
  SOLAR_CREDIT_HUMAN_NOT_OFFERED_CODE,
  SOLAR_LIGHTREACH_NOT_OFFERED_CODE,
  SOLAR_LOAN_PRODUCT_REQUIRED_CODE,
  SOLAR_LOAN_PRODUCT_UNAVAILABLE_CODE,
  SOLAR_SUNGAGE_FUNDING_GROUP_MISSING_CODE,
  SOLAR_SUNGAGE_NOT_OFFERED_CODE,
  SOLAR_SUNGAGE_PROVIDER_FAILED_CODE,
  SOLAR_SUNGAGE_SELLER_NOT_CERTIFIED_CODE,
  solarProposalErrorCode,
  solarProposalErrorMessages,
} from "~/api/solar-proposal-errors"

import { LIGHTREACH_PRICING_OUT_OF_DATE } from "./solar-lightreach-copy"
import { describePricingError } from "../ProposalPresentation/pricing/refresh-pricing-action"

/**
 * Refusals the server explains: a financed plan the seller fixes, and
 * Sungage's `/payments` failing (V1's reason, such as an uncertified seller).
 * The message says what happened.
 */
const SELLER_OWNED_REFUSALS: ReadonlySet<string> = new Set([
  SOLAR_LOAN_PRODUCT_REQUIRED_CODE,
  SOLAR_LOAN_PRODUCT_UNAVAILABLE_CODE,
  SOLAR_CREDIT_HUMAN_NOT_OFFERED_CODE,
  SOLAR_SUNGAGE_NOT_OFFERED_CODE,
  SOLAR_SUNGAGE_FUNDING_GROUP_MISSING_CODE,
  SOLAR_SUNGAGE_PROVIDER_FAILED_CODE,
  SOLAR_SUNGAGE_SELLER_NOT_CERTIFIED_CODE,
  SOLAR_LIGHTREACH_NOT_OFFERED_CODE,
])

/**
 * The server's reason when it refused to price a Credit Human or Sungage plan
 * (no loan term, one no longer offered, the partner no longer offered, or
 * Sungage's quote failing), or a LightReach plan (its pricing out of date, or
 * LightReach not offered). Null for any other failure.
 */
export const solarPricingRefusal = (error: unknown): string | null => {
  // LightReach sets the price: Refresh Pricing waits on Get LightReach
  // pricing and a current offer, which the Loan Product step owns.
  if (isSolarLightReachPricingRequiredError(error)) {
    return LIGHTREACH_PRICING_OUT_OF_DATE
  }
  const code = solarProposalErrorCode(error)
  if (code === null || !SELLER_OWNED_REFUSALS.has(code)) {
    return null
  }
  const [message] = solarProposalErrorMessages(error)
  return message ?? null
}

/**
 * A Solar pricing command's failure. The refusals above show as sent;
 * anything else reads as pricing's usual failure.
 */
export const solarPricingErrorMessage = (error: unknown): string =>
  solarPricingRefusal(error) ?? describePricingError(error)
